/**
 * Collaboration Types
 * Type definitions for Y.js-based real-time collaboration
 */

import * as Y from 'yjs';

/**
 * Collaboration configuration
 */
export interface CollaborationConfig {
  /** Document ID for collaboration */
  documentId: string;
  
  /** User information */
  user: {
    id: string;
    name: string;
    color: string;
  };
  
  /** WebSocket URL for Y.js provider */
  websocketUrl?: string;
  
  /** Authentication token */
  authToken?: string;
  
  /** Enable offline mode */
  offline?: boolean;
  
  /** Optional: Pre-existing Y.Doc to use (if not provided, creates new one) */
  doc?: Y.Doc;
}

/**
 * Collaboration user presence
 */
export interface UserPresence {
  id: string;
  name: string;
  color: string;
  cursor?: {
    anchor: number;
    head: number;
  };
  selection?: {
    from: number;
    to: number;
  };
  lastActive: number;
}

/**
 * Collaboration sync status
 */
export type SyncStatus = 'connecting' | 'connected' | 'synced' | 'disconnected' | 'error';

/**
 * Collaboration events
 */
export interface CollaborationEvents {
  'status': (status: SyncStatus) => void;
  'synced': () => void;
  'users-changed': (users: UserPresence[]) => void;
  'error': (error: Error) => void;
}

/**
 * Y.js provider interface
 */
export interface YjsProvider {
  /** Y.js document */
  doc: Y.Doc;
  
  /** Connection status */
  status: SyncStatus;
  
  /** Active users */
  users: Map<string, UserPresence>;
  
  /** Connect to WebSocket */
  connect(): void;
  
  /** Disconnect from WebSocket */
  disconnect(): void;
  
  /** Destroy provider */
  destroy(): void;
  
  /** Update local user presence */
  updatePresence(presence: Partial<UserPresence>): void;
  
  /** Subscribe to events */
  on<K extends keyof CollaborationEvents>(event: K, handler: CollaborationEvents[K]): void;
  
  /** Unsubscribe from events */
  off<K extends keyof CollaborationEvents>(event: K, handler: CollaborationEvents[K]): void;
}

/**
 * Collaboration state for BaseApplication
 */
export interface CollaborationState {
  /** Is collaboration enabled */
  enabled: boolean;
  
  /** Y.js provider */
  provider?: YjsProvider;
  
  /** Sync status */
  status: SyncStatus;
  
  /** Active users */
  users: UserPresence[];
  
  /** Local user info */
  localUser?: UserPresence;
}

